import { type CSSProperties, type RefObject, useCallback, useState } from 'react';

import type { ModalOptions, TriggerPos } from '@/components/Select/types';
import { getDropdownPosWithPos, getDropdownPosWithTrigger } from '@/utils/dom';

export type DropdownPos = {
  top: number | 'auto';
  left: number | 'auto';
  right: number | 'auto';
  bottom: number | 'auto';
};

type TriggerConfig = {
  // ref, el, pos 셋 중 하나만 있어야 함
  ref?: RefObject<HTMLElement>;
  el?: HTMLElement | null;
  pos?: TriggerPos;

  options?: ModalOptions;
};

const isSameDropdownPos = (
  prev: CSSProperties,
  next: CSSProperties,
) => {
  return (
    prev.top === next.top &&
    prev.left === next.left &&
    prev.right === next.right &&
    prev.bottom === next.bottom &&
    prev.height === next.height
  );
};

const useDropdownPos = (dropdownOpen: boolean, trigger: TriggerConfig) => {
  const [dropdownPos, setDropdownPos] = useState<CSSProperties>({});

  const setDropdownRef = useCallback(
    (dropdownEl: HTMLDivElement | null) => {
      if (!dropdownOpen) return;
      if (!dropdownEl) return;
      const el = trigger.ref?.current || trigger.el;
      if (el) {
        const { pos, modifiedHeight } = getDropdownPosWithTrigger(
          el,
          dropdownEl,
          trigger.options,
        );
        const nextPos = { ...pos, height: modifiedHeight ?? undefined };
        setDropdownPos((prev) => isSameDropdownPos(prev, nextPos) ? prev : nextPos);
        return;
      }

      if (trigger.pos?.top || trigger.pos?.left) {
        const { pos, modifiedHeight } = getDropdownPosWithPos(
          trigger.pos,
          dropdownEl,
        );

        const nextPos = { ...pos, height: modifiedHeight ?? undefined };
        setDropdownPos((prev) => isSameDropdownPos(prev, nextPos) ? prev : nextPos);
      }
    },
    [
      dropdownOpen,
      trigger.ref,
      trigger.el,
      trigger.pos?.left,
      trigger.pos?.top,
      trigger.options?.direction,
      trigger.options?.offset?.x,
      trigger.options?.offset?.y,
    ],
  );

  return { dropdownPos, setDropdownRef };
};

export default useDropdownPos;
